/**
 * The subset of CanvasRenderingContext2D that terrain generation draws with.
 * A real 2D context satisfies this structurally; MaskCanvas implements it
 * headlessly so the same generator code produces a bit-identical collision
 * mask in the browser and on the server.
 */
export interface TerrainDrawContext {
  fillStyle: string | CanvasGradient | CanvasPattern;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  lineWidth: number;
  globalAlpha: number;
  globalCompositeOperation: GlobalCompositeOperation;
  fillRect(x: number, y: number, w: number, h: number): void;
  strokeRect(x: number, y: number, w: number, h: number): void;
  clearRect(x: number, y: number, w: number, h: number): void;
  beginPath(): void;
  closePath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  quadraticCurveTo(cpx: number, cpy: number, x: number, y: number): void;
  ellipse(
    x: number,
    y: number,
    radiusX: number,
    radiusY: number,
    rotation: number,
    startAngle: number,
    endAngle: number,
  ): void;
  arc(x: number, y: number, radius: number, startAngle: number, endAngle: number): void;
  fill(): void;
  stroke(): void;
  save(): void;
  restore(): void;
  translate(x: number, y: number): void;
  rotate(angle: number): void;
}

interface Point {
  x: number;
  y: number;
}

interface Transform {
  a: number; // x scale / cos
  b: number; // y skew / sin
  c: number; // x skew / -sin
  d: number; // y scale / cos
  e: number; // x translate
  f: number; // y translate
}

const IDENTITY: Transform = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
const QUAD_SEGMENTS = 16;

/**
 * Deterministic software rasterizer for the terrain collision mask.
 * Tracks only the alpha channel (0..255); colors are irrelevant to collision.
 * Compositing semantics mirror canvas: source-over accumulates alpha,
 * destination-out erases, source-atop leaves coverage unchanged.
 */
export class MaskCanvas implements TerrainDrawContext {
  readonly width: number;
  readonly height: number;
  private alpha: Uint8Array;

  fillStyle: string | CanvasGradient | CanvasPattern = '#000000';
  strokeStyle: string | CanvasGradient | CanvasPattern = '#000000';
  lineWidth = 1;
  globalAlpha = 1;
  globalCompositeOperation: GlobalCompositeOperation = 'source-over';

  private transform: Transform = { ...IDENTITY };
  private transformStack: Transform[] = [];
  private subpaths: Point[][] = [];
  private currentSubpath: Point[] | null = null;

  constructor(width: number, height: number, alpha?: Uint8Array) {
    this.width = width;
    this.height = height;
    if (alpha) {
      if (alpha.length !== width * height) {
        throw new Error(
          `MaskCanvas: expected buffer of ${width * height} bytes, got ${alpha.length}`,
        );
      }
      this.alpha = alpha;
    } else {
      this.alpha = new Uint8Array(width * height);
    }
  }

  getAlpha(): Uint8Array {
    return this.alpha;
  }

  isSolid(x: number, y: number): boolean {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    if (ix < 0 || ix >= this.width || iy < 0 || iy >= this.height) return false;
    return this.alpha[iy * this.width + ix] > 128;
  }

  // ----- transforms -----

  save(): void {
    this.transformStack.push({ ...this.transform });
  }

  restore(): void {
    const t = this.transformStack.pop();
    if (t) this.transform = t;
  }

  translate(x: number, y: number): void {
    const t = this.transform;
    t.e += t.a * x + t.c * y;
    t.f += t.b * x + t.d * y;
  }

  rotate(angle: number): void {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const t = this.transform;
    const a = t.a * cos + t.c * sin;
    const b = t.b * cos + t.d * sin;
    const c = t.c * cos - t.a * sin;
    const d = t.d * cos - t.b * sin;
    t.a = a;
    t.b = b;
    t.c = c;
    t.d = d;
  }

  private apply(x: number, y: number): Point {
    const t = this.transform;
    return {
      x: t.a * x + t.c * y + t.e,
      y: t.b * x + t.d * y + t.f,
    };
  }

  // ----- path building -----

  beginPath(): void {
    this.subpaths = [];
    this.currentSubpath = null;
  }

  closePath(): void {
    if (this.currentSubpath && this.currentSubpath.length > 0) {
      const first = this.currentSubpath[0];
      this.currentSubpath.push({ x: first.x, y: first.y });
    }
  }

  moveTo(x: number, y: number): void {
    this.currentSubpath = [this.apply(x, y)];
    this.subpaths.push(this.currentSubpath);
  }

  lineTo(x: number, y: number): void {
    if (!this.currentSubpath) {
      this.moveTo(x, y);
      return;
    }
    this.currentSubpath.push(this.apply(x, y));
  }

  quadraticCurveTo(cpx: number, cpy: number, x: number, y: number): void {
    if (!this.currentSubpath || this.currentSubpath.length === 0) {
      this.moveTo(cpx, cpy);
    }
    const start = this.currentSubpath![this.currentSubpath!.length - 1];
    const cp = this.apply(cpx, cpy);
    const end = this.apply(x, y);
    for (let i = 1; i <= QUAD_SEGMENTS; i++) {
      const t = i / QUAD_SEGMENTS;
      const mt = 1 - t;
      this.currentSubpath!.push({
        x: mt * mt * start.x + 2 * mt * t * cp.x + t * t * end.x,
        y: mt * mt * start.y + 2 * mt * t * cp.y + t * t * end.y,
      });
    }
  }

  ellipse(
    x: number,
    y: number,
    radiusX: number,
    radiusY: number,
    rotation: number,
    startAngle: number,
    endAngle: number,
  ): void {
    const sweep = endAngle - startAngle;
    const maxR = Math.max(Math.abs(radiusX), Math.abs(radiusY));
    const segments = Math.max(
      8,
      Math.min(96, Math.ceil(((maxR * Math.abs(sweep)) / (Math.PI * 2)) * 1.5 + 8)),
    );
    const cosR = Math.cos(rotation);
    const sinR = Math.sin(rotation);

    for (let i = 0; i <= segments; i++) {
      const angle = startAngle + (sweep * i) / segments;
      const ex = Math.cos(angle) * radiusX;
      const ey = Math.sin(angle) * radiusY;
      const px = x + ex * cosR - ey * sinR;
      const py = y + ex * sinR + ey * cosR;
      if (i === 0) {
        // Canvas connects the current point to the arc start with a line.
        if (this.currentSubpath && this.currentSubpath.length > 0) {
          this.lineTo(px, py);
        } else {
          this.moveTo(px, py);
        }
      } else {
        this.lineTo(px, py);
      }
    }
  }

  arc(x: number, y: number, radius: number, startAngle: number, endAngle: number): void {
    this.ellipse(x, y, radius, radius, 0, startAngle, endAngle);
  }

  // ----- rasterization -----

  fill(): void {
    this.fillPolygons(this.subpaths, this.globalAlpha);
  }

  stroke(): void {
    const polys = this.strokePolygons(this.subpaths, this.lineWidth);
    for (const poly of polys) {
      this.fillPolygons([poly], this.globalAlpha);
    }
  }

  fillRect(x: number, y: number, w: number, h: number): void {
    const poly = [
      this.apply(x, y),
      this.apply(x + w, y),
      this.apply(x + w, y + h),
      this.apply(x, y + h),
    ];
    this.fillPolygons([poly], this.globalAlpha);
  }

  strokeRect(x: number, y: number, w: number, h: number): void {
    const corners = [
      this.apply(x, y),
      this.apply(x + w, y),
      this.apply(x + w, y + h),
      this.apply(x, y + h),
    ];
    const ring = [...corners, { x: corners[0].x, y: corners[0].y }];
    const polys = this.strokePolygons([ring], this.lineWidth);
    for (const poly of polys) {
      this.fillPolygons([poly], this.globalAlpha);
    }
  }

  clearRect(x: number, y: number, w: number, h: number): void {
    const poly = [
      this.apply(x, y),
      this.apply(x + w, y),
      this.apply(x + w, y + h),
      this.apply(x, y + h),
    ];
    const prevOp = this.globalCompositeOperation;
    const prevAlpha = this.globalAlpha;
    this.globalCompositeOperation = 'destination-out';
    this.globalAlpha = 1;
    this.fillPolygons([poly], 1);
    this.globalCompositeOperation = prevOp;
    this.globalAlpha = prevAlpha;
  }

  /** Expand each subpath's segments into thick quads plus joint discs. */
  private strokePolygons(subpaths: Point[][], lineWidth: number): Point[][] {
    const half = Math.max(0.5, lineWidth / 2);
    const polys: Point[][] = [];

    for (const path of subpaths) {
      for (let i = 0; i < path.length - 1; i++) {
        const p0 = path[i];
        const p1 = path[i + 1];
        const dx = p1.x - p0.x;
        const dy = p1.y - p0.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len === 0) continue;
        const nx = (-dy / len) * half;
        const ny = (dx / len) * half;
        polys.push([
          { x: p0.x + nx, y: p0.y + ny },
          { x: p1.x + nx, y: p1.y + ny },
          { x: p1.x - nx, y: p1.y - ny },
          { x: p0.x - nx, y: p0.y - ny },
        ]);
      }
      // Joint discs to approximate joins.
      if (lineWidth > 2) {
        for (let i = 1; i < path.length - 1; i++) {
          polys.push(this.discPolygon(path[i].x, path[i].y, half));
        }
      }
    }
    return polys;
  }

  private discPolygon(cx: number, cy: number, r: number): Point[] {
    const segments = 10;
    const poly: Point[] = [];
    for (let i = 0; i < segments; i++) {
      const angle = (Math.PI * 2 * i) / segments;
      poly.push({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r });
    }
    return poly;
  }

  /** Non-zero winding scanline fill across all subpaths (device space). */
  private fillPolygons(subpaths: Point[][], alpha: number): void {
    let minY = Infinity;
    let maxY = -Infinity;
    for (const path of subpaths) {
      for (const p of path) {
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
    }
    if (minY === Infinity) return;

    const yStart = Math.max(0, Math.floor(minY));
    const yEnd = Math.min(this.height - 1, Math.ceil(maxY));
    const crossings: Array<{ x: number; dir: number }> = [];

    for (let py = yStart; py <= yEnd; py++) {
      const sampleY = py + 0.5;
      crossings.length = 0;

      for (const path of subpaths) {
        const n = path.length;
        if (n < 2) continue;
        for (let i = 0; i < n; i++) {
          const p0 = path[i];
          const p1 = i === n - 1 ? path[0] : path[i + 1]; // implicit close for filling
          if (p0.y === p1.y) continue;
          const ymin = Math.min(p0.y, p1.y);
          const ymax = Math.max(p0.y, p1.y);
          if (sampleY < ymin || sampleY >= ymax) continue;
          const t = (sampleY - p0.y) / (p1.y - p0.y);
          crossings.push({
            x: p0.x + t * (p1.x - p0.x),
            dir: p1.y > p0.y ? 1 : -1,
          });
        }
      }

      if (crossings.length === 0) continue;
      crossings.sort((a, b) => a.x - b.x);

      let winding = 0;
      let spanStart = 0;
      for (const crossing of crossings) {
        const prevWinding = winding;
        winding += crossing.dir;
        if (prevWinding === 0 && winding !== 0) {
          spanStart = crossing.x;
        } else if (prevWinding !== 0 && winding === 0) {
          this.fillSpan(py, spanStart, crossing.x, alpha);
        }
      }
    }
  }

  private fillSpan(py: number, x0: number, x1: number, alpha: number): void {
    const xStart = Math.max(0, Math.round(x0));
    const xEnd = Math.min(this.width, Math.round(x1));
    if (xEnd <= xStart) return;

    const rowOffset = py * this.width;
    const op = this.globalCompositeOperation;
    const sa = Math.max(0, Math.min(1, alpha));

    if (op === 'destination-out') {
      const keep = 1 - sa;
      for (let x = xStart; x < xEnd; x++) {
        this.alpha[rowOffset + x] = Math.round(this.alpha[rowOffset + x] * keep);
      }
    } else if (op === 'source-atop') {
      // Coverage unchanged: drawing only recolors existing solid pixels.
      return;
    } else {
      // source-over
      const src = sa * 255;
      const keep = 1 - sa;
      for (let x = xStart; x < xEnd; x++) {
        this.alpha[rowOffset + x] = Math.min(
          255,
          Math.round(src + this.alpha[rowOffset + x] * keep),
        );
      }
    }
  }
}
